import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ShieldCheck, Search, History, User as UserIcon, Activity } from "lucide-react";

const ENTITY_OPTIONS = [
  "", "students", "teachers", "classes", "subjects", "stages", "grades",
  "attendance", "behavior", "fees", "invoices", "payments",
  "announcements", "homework", "messages",
  "vehicles", "drivers", "routes", "trips",
  "employees", "leave_requests", "payroll_items",
  "users", "schools", "settings",
];

function actionColor(action) {
  if (!action) return "bg-muted text-muted-foreground";
  if (action.includes("delete")) return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300";
  if (action.includes("create")) return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300";
  if (action.includes("update") || action.includes("edit")) return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300";
  if (action.startsWith("login") || action.startsWith("logout")) return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300";
  if (action.startsWith("leave") || action.startsWith("checkin")) return "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300";
  return "bg-muted text-foreground";
}

export default function AuditLogsPage() {
  const { t, lang } = useI18n();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [entity, setEntity] = useState("");
  const [actor, setActor] = useState("");
  const [days, setDays] = useState(30);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const userMap = useMemo(() => {
    const m = {}; users.forEach((u) => { m[u.id] = u; }); return m;
  }, [users]);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ days: String(days), limit: "500" });
    if (entity) params.set("entity", entity);
    if (actor) params.set("actor_id", actor);
    api.get(`/audit-logs?${params.toString()}`)
      .then((r) => setLogs(r.data || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [entity, actor, days]);

  const filtered = useMemo(() => {
    if (!q) return logs;
    const needle = q.toLowerCase();
    return logs.filter((l) =>
      (l.action || "").toLowerCase().includes(needle) ||
      (l.entity || "").toLowerCase().includes(needle) ||
      (userMap[l.actor_id]?.email || "").toLowerCase().includes(needle) ||
      (userMap[l.actor_id]?.name || "").toLowerCase().includes(needle) ||
      JSON.stringify(l.meta || {}).toLowerCase().includes(needle)
    );
  }, [logs, q, userMap]);

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB", { dateStyle: "short", timeStyle: "medium" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6">
      {/* Hero strip */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="relative bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground p-6 sm:p-8">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 80% 0%, rgba(255,255,255,.4), transparent 40%)" }} />
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur grid place-items-center shrink-0">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">{t("audit_logs")}</h1>
              <p className="text-sm text-primary-foreground/80 mt-1">{t("audit_logs_desc")}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-5 border shadow-none">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute top-3 start-3 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t("search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="ps-9"
              data-testid="audit-search"
            />
          </div>
          <Select value={entity || "__all"} onValueChange={(v) => setEntity(v === "__all" ? "" : v)}>
            <SelectTrigger data-testid="audit-entity"><SelectValue placeholder={t("entity")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{t("any")} — {t("entity")}</SelectItem>
              {ENTITY_OPTIONS.filter(Boolean).map((e) => (
                <SelectItem key={e} value={e}>{e}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={actor || "__all"} onValueChange={(v) => setActor(v === "__all" ? "" : v)}>
            <SelectTrigger data-testid="audit-actor"><SelectValue placeholder={t("actor")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{t("any")} — {t("actor")}</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name || u.email} <span className="text-xs text-muted-foreground">({u.role})</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger data-testid="audit-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[7, 14, 30, 60, 90].map((n) => (
                <SelectItem key={n} value={String(n)}>{t("last_days").replace("{n}", n)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3 text-xs text-muted-foreground flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" />
          {loading ? "..." : `${filtered.length} / ${logs.length}`}
        </div>
      </Card>

      {/* Logs list */}
      <Card className="border shadow-none divide-y divide-border">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <History className="w-10 h-10 mx-auto mb-3 opacity-40" />
            {t("no_data") || "No data"}
          </div>
        ) : (
          filtered.map((l) => {
            const u = userMap[l.actor_id];
            return (
              <div key={l.id} className="p-4 sm:p-5 hover:bg-muted/30 smooth grid sm:grid-cols-12 gap-3 items-start" data-testid={`audit-row-${l.id}`}>
                <div className="sm:col-span-3 flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                    <UserIcon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{u?.name || l.actor_id?.slice(0, 8) || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate">{u?.email || "—"} <span className="font-mono">· {u?.role || "?"}</span></div>
                  </div>
                </div>
                <div className="sm:col-span-3 flex flex-wrap items-center gap-2">
                  <Badge className={`${actionColor(l.action)} border-0 font-mono text-xs`}>{l.action}</Badge>
                  {l.entity && <Badge variant="outline" className="font-mono text-xs">{l.entity}</Badge>}
                </div>
                <div className="sm:col-span-4 text-xs">
                  {l.meta && Object.keys(l.meta).length > 0 ? (
                    <pre className="font-mono text-[11px] text-muted-foreground bg-muted/40 p-2 rounded-md overflow-x-auto max-h-24">
                      {JSON.stringify(l.meta, null, 2)}
                    </pre>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
                <div className="sm:col-span-2 text-xs text-muted-foreground font-mono text-end">{fmt(l.at)}</div>
              </div>
            );
          })
        )}
      </Card>
    </div>
  );
}
